import { GridColDef } from "@mui/x-data-grid";
import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CustomLinkNavigate } from "../../../../SharedComponents/CustomLinkNavigate";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { ExploreHealthTooltipTexts } from "../../Common/HealthView.constants";
import { renderHeaderWithTooltip, renderScoreCell } from "../../Common/HealthView.common";

const HealthOverviewTable = (props: any) => {
  const isTPIDView = props.viewMode === "tpid";

  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{"Health Overview details are not available."} </div>;
  };

  const columns: GridColDef[] = [
    ...(!isTPIDView ? [{
      headerName: "Tenant Name",
      field: "tenantName",
      sortable: false,
      flex: 1.5,
      minWidth: 200,
      renderHeader: (params: any) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.TenantName),
      renderCell: ({ row }: any) =>
        row.overall_CustomerHealthScore == null && row.overall_SupportScore == null ? (
          <span>{row.tenantName}</span>
        ) : (
          <CustomLinkNavigate to={navigateToTenant(row.tenantId)} text={row.tenantName} />
        )
    }] : []),
    {
      headerName: "TPID",
      field: "tpid",
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      sortable: true,
      renderHeader: (params: any) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.TPID)
    },
    {
      headerName: "TPAccountName",
      field: "tpAccountName",
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      renderHeader: (params: any) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.TPAccountName),
      ...(isTPIDView ? {
        renderCell: ({ row }: any) =>
          row.overall_CustomerHealthScore == null && row.overall_SupportScore == null ? (
            <span>{row.tpAccountName}</span>
          ) : (
            <span>{row.tpAccountName}</span>
          )
      } : {})
    },
    {
      headerName: "ExpirationDate",
      field: "expirationDate",
      flex: 1.5,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.ExpirationDate)
    },
    {
      headerName: "Customer Health Score",
      field: "overall_CustomerHealthScore",
      type: "number",
      flex: 1.2,
      minWidth: 250,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CustomerHealthScore)
    },
    {
      headerName: "SHI Score",
      field: "overall_SupportScore",
      type: "number",
      flex: 1.2,
      minWidth: 160,
      sortable: true,
      headerAlign: "center",
      align: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.SHIScore)
    },
    {
      headerName: "Catalyst",
      field: "catalystCustomer",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.Catalyst),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.catalystCustomer === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Mission Critical Services",
      field: "missionCriticalServices",
      flex: 1,
      minWidth: 270,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.MissionCritical),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.missionCriticalServices === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "CxE IDNA IDCAT",
      field: "idcat",
      flex: 1,
      minWidth: 250,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IDCAT),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.idcat === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Intensive Care",
      field: "intensiveCare",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IntensiveCare),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.intensiveCare === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Intune Marquee",
      field: "intuneMarque",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IntuneMarque),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.intuneMarque === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Purview BwC",
      field: "purviewBwC",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.PurviewBwC),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%", justifyContent: "center" }}>
          {row.purviewBwC === "No" ? (
            <CloseIcon
              titleAccess="No"
              sx={{
                color: "red",
                fontSize: "1.3rem"
              }}
            />
          ) : (
            <CheckIcon
              titleAccess="Yes"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Active SR",
      type: "number",
      field: "overall_ActiveSupportRequests",
      flex: 1,
      minWidth: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.ActiveSR)
    },
    {
      headerName: "Critical Cases",
      type: "number",
      field: "overall_CritSitCases",
      flex: 1,
      minWidth: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CriticalCases)
    },
    {
      headerName: "UAT",
      type: "number",
      field: "overall_UATFeatureRequests",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.UAT)
    },
    {
      headerName: "CRM",
      type: "number",
      field: "overall_CRMFeatureRequests",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.CRM)
    },
    {
      headerName: "DCR",
      type: "number",
      field: "overall_DCRFeatureRequests",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.DCR)
    },
    {
      headerName: "MTP Health Score",
      field: "mtP_HealthScore",
      type: "number",
      flex: 1.2,
      minWidth: 250,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.MTPHealthScore)
    },
    {
      headerName: "MTP Support Health Score",
      field: "mtP_SupportScore",
      type: "number",
      flex: 1.5,
      minWidth: 280,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.MTPSupportHealthScore)
    },
    {
      headerName: "Purview Health Score",
      field: "purview_HealthScore",
      type: "number",
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.PurviewHealthScore)
    },
    {
      headerName: "Purview Support Health Score",
      field: "purview_SupportScore",
      type: "number",
      flex: 1.5,
      minWidth: 290,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.PurviewSupportHealthScore)
    },
    {
      headerName: "Intune Health Score",
      field: "intune_HealthScore",
      type: "number",
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IntuneHealthScore)
    },
    {
      headerName: "Intune Support Health Score",
      field: "intune_SupportScore",
      type: "number",
      flex: 1.5,
      minWidth: 290,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, ExploreHealthTooltipTexts.IntuneSupportHealthScore)
    }
  ];

  const scoreStatusByField: Record<string, string> = {
    overall_CustomerHealthScore: "overall_CustomerHealthScoreStatus",
    overall_SupportScore: "overall_SupportHealthScoreStatus",
    mtP_HealthScore: "mtP_CustomerHealthScoreStatus",
    mtP_SupportScore: "mtP_SupportHealthScoreStatus",
    purview_HealthScore: "purview_CustomerHealthScoreStatus",
    purview_SupportScore: "purview_SupportHealthScoreStatus",
    intune_HealthScore: "intune_CustomerHealthScoreStatus",
    intune_SupportScore: "intune_SupportHealthScoreStatus"
  };

  const columnsWithScoreFormatting = columns.map((column) => {
    if (typeof column.field !== "string" || !column.field.toLowerCase().includes("score")) {
      return column;
    }

    return {
      ...column,
      cellClassName: "score-cell",
      renderCell: ({ row }: any) =>
        renderScoreCell(
          row[column.field],
          scoreStatusByField[column.field] ? row[scoreStatusByField[column.field]] : undefined
        )
    };
  });

  const tickColumnFields = new Set(["catalystCustomer", "missionCriticalServices", "idcat", "intensiveCare", "intuneMarque", "purviewBwC"]);
  const keyColumnFields = ["tenantName", "tpid", "tpAccountName", "expirationDate"];
  const scoreColumns = columnsWithScoreFormatting.filter((column) => typeof column.field === "string" && column.field.toLowerCase().includes("score"));
  const tickColumns = columnsWithScoreFormatting.filter((column) => typeof column.field === "string" && tickColumnFields.has(column.field));
  const otherColumns = columnsWithScoreFormatting.filter(
    (column) =>
      typeof column.field === "string" &&
      !keyColumnFields.includes(column.field) &&
      !column.field.toLowerCase().includes("score") &&
      !tickColumnFields.has(column.field)
  );

  const orderedColumns = [
    ...columnsWithScoreFormatting.filter((column) => typeof column.field === "string" && keyColumnFields.includes(column.field)),
    ...scoreColumns,
    ...tickColumns,
    ...otherColumns
  ];

  const columnsForCompactView = orderedColumns.map((column) => {
    const field = typeof column.field === "string" ? column.field : "";
    if (field === "tenantName") {
      return {
        ...column,
        flex: 0.95,
        minWidth: 140
      };
    }

    if (field === "tpAccountName") {
      return {
        ...column,
        flex: 1,
        minWidth: 160
      };
    }

    if (field === "tpid") {
      const { flex, ...columnWithoutFlex } = column;
      return {
        ...columnWithoutFlex,
        width: 72,
        minWidth: 72,
        maxWidth: 72
      };
    }

    if (field === "expirationDate") {
      return {
        ...column,
        flex: 0.8,
        minWidth: 110
      };
    }

    if (tickColumnFields.has(field)) {
      const { flex, ...columnWithoutFlex } = column;
      return {
        ...columnWithoutFlex,
        width: 78,
        minWidth: 78,
        maxWidth: 78
      };
    }

    if (column.type === "number") {
      const { flex, ...columnWithoutFlex } = column;
      return {
        ...columnWithoutFlex,
        width: 92,
        minWidth: 92,
        maxWidth: 92
      };
    }

    return {
      ...column,
      flex: 0.8,
      minWidth: 110
    };
  });

  const tableColumns = columnsForCompactView;

  React.useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName("MuiDataGrid-virtualScroller");
      Array.from(elements).forEach(function (element) {
        element.setAttribute("tabIndex", "0");
      });
    }, 1000);
  }, []);

  return (
    <>
      {props.tableData && props.tableData.length > 0 ? (
        <div style={{ height: 600, width: "100%" }} tabIndex={0}>
          <CustomDataGrid
            columns={tableColumns}
            columnHeaderHeight={112}
            enableUrlParams
            exportCsvButtonProps={{
              fileNamePrefix: "support-requests"
            }}
            getRowId={(row: any) => isTPIDView ? row.tpid : row.tenantId}
            rows={{ values: props.tableData ?? [], count: props.tableData?.length ?? 0 }}
            showCellVerticalBorder
            sx={{
              "& .MuiDataGrid-cell.score-cell": {
                padding: "0 !important"
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                justifyContent: "center"
              },
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                justifyContent: "center",
                overflow: "visible"
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: "1.2",
                textOverflow: "clip",
                overflow: "visible"
              }
            }}
            kustoLink={props?.kustoLink}
          />
        </div>
      ) : (
        showNoData() || ""
      )}
    </>
  );
};

function navigateToTenant(tenantId: string) {
  return `/customer?id=${tenantId}&tab=customerHealth&section=MTP&from=explorehealth`;
}

export default HealthOverviewTable;